import { format } from "date-fns"
import { de } from "date-fns/locale"

/**
 * Format ETA for display, in Europe/Berlin time regardless of viewer location.
 *
 * Inspections happen in Germany, so the customer-facing time must be Berlin
 * local. Without an explicit timezone, a customer abroad sees their phone's
 * local time instead — wrong for an order taking place in Berlin.
 *
 * Uses Intl.DateTimeFormat (no extra dep) instead of date-fns/format which
 * doesn't support timezone conversion natively.
 *
 * Accepts null/undefined for defensive use-cases (WEB-1R: date is null in
 * widget module context). Returns empty string for nullish input.
 */
export const formatETA = (date: Date | null | undefined): string => {
  if (!date) return ""
  try {
    const hhmm = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date)
    return `${hhmm} Uhr`
  } catch {
    return "--:--"
  }
}

/**
 * Format date and time.
 *
 * Accepts null/undefined for defensive use-cases (WEB-1R: date is null in
 * widget module context). Returns empty string for nullish input.
 */
export const formatDateTime = (date: Date | null | undefined): string => {
  if (!date) return ""
  try {
    return format(date, "dd.MM.yyyy HH:mm", { locale: de })
  } catch {
    return ""
  }
}

/**
 * Format duration in minutes
 */
export const formatDuration = (seconds: number): string => {
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) {
    return `${minutes} Min.`
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  return `${hours} Std. ${mins} Min.`
}

/**
 * Format distance in meters/kilometers
 */
export const formatDistance = (meters: number): string => {
  if (meters < 1000) {
    return `${meters} m`
  }
  const km = (meters / 1000).toFixed(1)
  return `${km} km`
}
